import {
  getApiUserManagementV1AuditLogEntryId,
  postApiUserManagementV1AuditLogEntryIdReview
} from '@/libs/api-user-mgt/generated/audit-log/audit-log';
import {
  getApiUserManagementV1AuditLogExport,
  type AuditLogExportFormat
} from '@/libs/api-user-mgt/umOpenApiGaps/auditExportNotInOpenApiClient';

import { type AuditWireDto, mapAuditDtoToEntity } from '@/userManagement/application/mappers/auditMapper';
import type { AuditLogEntry } from '@/userManagement/domain/entities/AuditLogEntry';
import type { AuditListParams, PaginatedResponse } from '@/userManagement/domain/types/QueryContracts';

import { fetchUserManagementPaged } from './pagination';
import { toUserManagementAuditQueryParams } from './sieveQuery';

/** OpenAPI client uses `void` response generics; UM returns audit DTO bodies at runtime. */
function auditResponseData(data: unknown): AuditWireDto {
  return data as unknown as AuditWireDto;
}

function parseContentDispositionFilename(header: string | undefined, fallback: string): string {
  if (header == null || header.trim() === '') return fallback;
  const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(header);
  if (match?.[1]) {
    try {
      return decodeURIComponent(match[1].replace(/"/g, '').trim());
    } catch {
      return fallback;
    }
  }
  return fallback;
}

export const userManagementAuditSegment = {
  listAuditLog: (params: AuditListParams): Promise<PaginatedResponse<AuditWireDto>> =>
    fetchUserManagementPaged<AuditWireDto>('auditLog', params as Record<string, unknown>),

  exportAuditLog: async (params: AuditListParams, format: AuditLogExportFormat): Promise<{ blob: Blob; filename: string }> => {
    const query = toUserManagementAuditQueryParams(params);
    const response = await getApiUserManagementV1AuditLogExport({
      format,
      sorts: query.sorts,
      filters: query.filters,
      dateFrom: query.dateFrom,
      dateTo: query.dateTo,
      defaultView: query.defaultView
    });

    const extension = format === 'xlsx' ? 'xlsx' : 'csv';
    const fallbackFilename = `audit-log-${new Date().toISOString().slice(0, 10)}.${extension}`;
    const disposition =
      typeof response.headers === 'object' && response.headers != null && 'content-disposition' in response.headers
        ? String(response.headers['content-disposition'])
        : undefined;

    return {
      blob: response.data,
      filename: parseContentDispositionFilename(disposition, fallbackFilename)
    };
  },

  getAuditEntry: async (entryId: number): Promise<AuditLogEntry> => {
    const r = await getApiUserManagementV1AuditLogEntryId(entryId);
    return mapAuditDtoToEntity(auditResponseData(r.data));
  },

  reviewAuditEntry: async (entryId: number): Promise<AuditLogEntry> => {
    await postApiUserManagementV1AuditLogEntryIdReview(entryId);
    const r = await getApiUserManagementV1AuditLogEntryId(entryId);
    return mapAuditDtoToEntity(auditResponseData(r.data));
  }
};
